import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useFDClient,
  useFDVisits,
  useFDReferrals,
  useUpdateFDReferral,
  useUpdateFDVisit,
  useLinkFDClient,
} from '../hooks/useFoodDistribution';
import { useSearchIndividuals } from '../hooks/useIndividuals';
import { FDVisit, FDReferral } from '../types/foodDistribution';
import '../styles/FoodDistribution.css';

function badgeForCategory(cat: string | undefined) {
  if (!cat) {
    return null;
  }
  const lower = cat.toLowerCase();
  if (lower === 'high') {
    return <span className="fd-badge fd-badge-high">High / Marginal</span>;
  }
  if (lower === 'low') {
    return <span className="fd-badge fd-badge-low">Low</span>;
  }
  if (lower === 'verylow') {
    return <span className="fd-badge fd-badge-verylow">Very Low</span>;
  }
  return <span className="fd-badge">{cat}</span>;
}

export function FDClientDetailPage() {
  const { fdClientId } = useParams<{ fdClientId: string }>();
  const id = fdClientId ? parseInt(fdClientId) : undefined;

  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkIndividualId, setLinkIndividualId] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: client, isLoading: clientLoading, error: clientError } = useFDClient(id);
  const { data: visitsData, isLoading: visitsLoading } = useFDVisits(id);
  const { data: referralsData, isLoading: referralsLoading } = useFDReferrals(
    id ? { page: 1, pageSize: 50 } : undefined
  );

  const { mutate: updateReferral, isPending: savingReferral } = useUpdateFDReferral();
  const { mutate: updateVisit, isPending: savingVisit } = useUpdateFDVisit();
  const linkMutation = useLinkFDClient();

  // Edit visit modal state
  const [editingVisit, setEditingVisit] = useState<FDVisit | null>(null);
  const [editStaff, setEditStaff] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Edit referral modal state
  const [editingReferral, setEditingReferral] = useState<FDReferral | null>(null);
  const [editReferralStatus, setEditReferralStatus] = useState('');
  const [editReferralNotes, setEditReferralNotes] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: searchResults, isLoading: searching } = useSearchIndividuals(
    debouncedSearch,
    1,
    10
  );

  const visits = visitsData?.data ?? [];
  const allReferrals = referralsData?.data ?? [];
  // Filter referrals to this client
  const referrals = allReferrals.filter((r) => r.fdClientId === id);

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkError('');
    const idNum = parseInt(linkIndividualId, 10);
    if (!id || isNaN(idNum)) {
      setLinkError('Please enter a valid Individual ID.');
      return;
    }
    try {
      await linkMutation.mutateAsync({ id, individualId: idNum });
      setLinkSuccess(true);
      setShowLinkForm(false);
      setLinkIndividualId('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to link client.';
      setLinkError(message);
    }
  };

  const openEditVisit = (visit: FDVisit) => {
    setEditingVisit(visit);
    setEditStaff(visit.staffUser || '');
    setEditNotes(visit.notes || '');
  };

  const handleSaveVisit = () => {
    if (!editingVisit) {
      return;
    }
    updateVisit(
      { id: editingVisit.fdVisitId, data: { staffUser: editStaff, notes: editNotes } },
      { onSuccess: () => setEditingVisit(null) }
    );
  };

  const openEditReferral = (referral: FDReferral) => {
    setEditingReferral(referral);
    setEditReferralStatus(referral.engagementStatus);
    setEditReferralNotes(referral.staffNotes || '');
  };

  const handleSaveReferral = () => {
    if (!editingReferral) {
      return;
    }
    updateReferral(
      {
        id: editingReferral.fdReferralId,
        data: {
          engagementStatus: editReferralStatus,
          staffNotes: editReferralNotes,
          ...(editReferralStatus === 'Engaged' && editingReferral.engagementStatus !== 'Engaged'
            ? { engagementDate: new Date().toISOString().slice(0, 10) }
            : {}),
          ...(editReferralStatus === 'Completed' && editingReferral.engagementStatus !== 'Completed'
            ? { completionDate: new Date().toISOString().slice(0, 10) }
            : {}),
        },
      },
      { onSuccess: () => setEditingReferral(null) }
    );
  };

  if (clientLoading) {
    return (
      <div>
        <Link to="/food-distribution">← Back to Clients</Link>
        <p style={{ marginTop: '1rem', color: '#888' }}>Loading client...</p>
      </div>
    );
  }

  if (clientError || !client) {
    return (
      <div>
        <Link to="/food-distribution">← Back to Clients</Link>
        <p style={{ marginTop: '1rem', color: '#c62828' }}>Client not found or failed to load.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/food-distribution" className="fd-table-link">
          ← Back to Clients
        </Link>
      </div>

      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <h2 style={{ marginBottom: '0.25rem' }}>
            {client.firstName} {client.lastName}
          </h2>
          <p style={{ margin: 0 }}>
            UID: {client.clientUid} · Phone: {client.phone}
          </p>
        </div>
        {!client.individualId && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className="button-secondary"
              onClick={() => {
                setShowLinkForm(!showLinkForm);
                setLinkError('');
              }}
              style={{ whiteSpace: 'nowrap' }}
            >
              {showLinkForm ? 'Cancel' : 'Link to CRM Client'}
            </button>
            <Link
              to={`/clients/intake?fdClientId=${client.fdClientId}`}
              className="button-primary"
              style={{
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Create New CRM Client
            </Link>
          </div>
        )}
      </div>

      {/* Link to CRM form */}
      {showLinkForm && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', color: '#5B2C86', fontSize: '1rem' }}>
            Link to CRM Individual
          </h3>
          <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Search by name or enter the CRM Individual ID directly.
          </p>

          {/* Name search */}
          <div ref={dropdownRef} style={{ position: 'relative', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#555' }}>
                Search by Name
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                  if (!e.target.value) {
                    setSelectedName('');
                    setLinkIndividualId('');
                  }
                }}
                placeholder="Type a name to search..."
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  width: 300,
                  fontFamily: 'Montserrat, sans-serif',
                }}
              />
            </label>
            {showDropdown && debouncedSearch.length >= 2 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxWidth: 400,
                  maxHeight: 220,
                  overflowY: 'auto',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  marginTop: 2,
                }}
              >
                {searching && (
                  <div style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem' }}>
                    Searching...
                  </div>
                )}
                {!searching && (!searchResults?.data || searchResults.data.length === 0) && (
                  <div style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem' }}>
                    No CRM clients found.
                  </div>
                )}
                {!searching &&
                  searchResults?.data?.map((ind) => (
                    <div
                      key={ind.individualID}
                      onClick={() => {
                        setLinkIndividualId(String(ind.individualID));
                        setSelectedName(`${ind.firstName} ${ind.lastName}`);
                        setSearchTerm(`${ind.firstName} ${ind.lastName}`);
                        setShowDropdown(false);
                      }}
                      style={{
                        padding: '0.6rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLDivElement).style.background = '#f5f0fa';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLDivElement).style.background = 'white';
                      }}
                    >
                      <span>
                        <strong>
                          {ind.firstName} {ind.lastName}
                        </strong>
                      </span>
                      <span style={{ color: '#888', fontSize: '0.8rem' }}>
                        {ind.clientID || `ID ${ind.individualID}`}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {selectedName && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 0.75rem',
                background: '#f5f0fa',
                borderRadius: 6,
                fontSize: '0.875rem',
                color: '#5B2C86',
              }}
            >
              Selected: <strong>{selectedName}</strong> (ID: {linkIndividualId})
            </div>
          )}

          <form
            onSubmit={handleLinkSubmit}
            style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#555' }}>
                Individual ID
              </span>
              <input
                type="number"
                value={linkIndividualId}
                onChange={(e) => {
                  setLinkIndividualId(e.target.value);
                  setSelectedName('');
                  setSearchTerm('');
                }}
                placeholder="e.g. 42"
                min={1}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  width: 160,
                  fontFamily: 'Montserrat, sans-serif',
                }}
                required
              />
            </label>
            <button className="button-primary" type="submit" disabled={linkMutation.isPending}>
              {linkMutation.isPending ? 'Linking...' : 'Link'}
            </button>
          </form>
          {linkError && (
            <p style={{ color: '#c62828', marginTop: '0.5rem', fontSize: '0.875rem' }}>
              {linkError}
            </p>
          )}
        </div>
      )}

      {linkSuccess && (
        <div
          style={{
            background: '#e8f5e9',
            border: '1px solid #a5d6a7',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            marginBottom: '1.25rem',
            color: '#2e7d32',
            fontSize: '0.9rem',
          }}
        >
          Successfully linked to CRM individual.
        </div>
      )}

      {/* Client info */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Client Information</h3>
        <div className="fd-detail-grid">
          <div className="fd-detail-field">
            <span className="fd-detail-label">UID</span>
            <span className="fd-detail-value">{client.clientUid}</span>
          </div>
          <div className="fd-detail-field">
            <span className="fd-detail-label">Phone</span>
            <span className="fd-detail-value">{client.phone}</span>
          </div>
          <div className="fd-detail-field">
            <span className="fd-detail-label">Zip Code</span>
            <span className="fd-detail-value">{client.zipCode}</span>
          </div>
          <div className="fd-detail-field">
            <span className="fd-detail-label">Household Size</span>
            <span className="fd-detail-value">{client.householdSize}</span>
          </div>
          <div className="fd-detail-field">
            <span className="fd-detail-label">Children</span>
            <span className="fd-detail-value">{client.childrenInHousehold}</span>
          </div>
          <div className="fd-detail-field">
            <span className="fd-detail-label">First Visit</span>
            <span className="fd-detail-value">
              {new Date(client.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div className="fd-detail-field" style={{ gridColumn: '1 / -1' }}>
            <span className="fd-detail-label">TEFAP Eligibility</span>
            <span className="fd-detail-value">
              {client.tefapEligibility &&
              Array.isArray(client.tefapEligibility) &&
              client.tefapEligibility.length > 0 ? (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {client.tefapEligibility.map((item) => (
                    <span
                      key={item}
                      style={{
                        background: '#ede7f6',
                        color: '#5B2C86',
                        padding: '0.2rem 0.6rem',
                        borderRadius: 12,
                        fontSize: '0.8rem',
                        fontWeight: 500,
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </span>
              ) : (
                <span style={{ color: '#999', fontStyle: 'italic' }}>Not answered</span>
              )}
            </span>
          </div>
          {client.individualId && client.crmClientId && (
            <div className="fd-detail-field">
              <span className="fd-detail-label">Linked CRM Client</span>
              <span className="fd-detail-value">
                <Link to={`/clients/${client.crmClientId}`} className="fd-table-link">
                  View in CRM
                </Link>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Visit history */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>
          Visit History ({visits.length} visit{visits.length !== 1 ? 's' : ''})
        </h3>

        {visitsLoading && <p style={{ color: '#888' }}>Loading visits...</p>}

        {!visitsLoading && visits.length === 0 && (
          <div className="fd-empty">
            <div className="fd-empty-icon">📋</div>
            <p>No visits recorded yet.</p>
          </div>
        )}

        {!visitsLoading && visits.length > 0 && (
          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Visit #</th>
                  <th>Date</th>
                  <th>Distribution</th>
                  <th>USDA Score</th>
                  <th>Food Security</th>
                  <th>Fruit/Veg Days</th>
                  <th>Needs</th>
                  <th>Staff</th>
                  <th>Notes</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v) => {
                  const needs = Array.isArray(v.serviceNeeds) ? v.serviceNeeds : [];
                  return (
                    <tr key={v.fdVisitId}>
                      <td>#{v.visitSequence}</td>
                      <td>{new Date(v.visitDatetime).toLocaleDateString()}</td>
                      <td>
                        <span
                          className={`fd-badge ${v.distributionType === 'Pantry' ? 'fd-badge-pantry' : v.distributionType === 'Food Co-op' ? 'fd-badge-coop' : ''}`}
                        >
                          {v.distributionType}
                        </span>
                      </td>
                      <td>
                        {v.usda6RawScore != null ? (
                          <div className="fd-usda-score-bar">
                            <div className="fd-usda-track">
                              <div
                                className={`fd-usda-fill ${
                                  v.usda6Category === 'VeryLow'
                                    ? 'fd-usda-fill-verylow'
                                    : v.usda6Category === 'Low'
                                      ? 'fd-usda-fill-low'
                                      : 'fd-usda-fill-high'
                                }`}
                                style={{ width: `${(v.usda6RawScore / 6) * 100}%` }}
                              />
                            </div>
                            <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                              {v.usda6RawScore}/6
                            </span>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{badgeForCategory(v.usda6Category)}</td>
                      <td>
                        {v.fruitvegDontknow
                          ? "Don't know"
                          : v.fruitvegDays != null
                            ? `${v.fruitvegDays} days`
                            : '—'}
                      </td>
                      <td>
                        {needs.length > 0 ? (
                          <span
                            title={needs.join(', ')}
                            className="fd-badge"
                            style={{ cursor: 'help', background: '#e8f5e9', color: '#2e7d32' }}
                          >
                            {needs.length} need{needs.length !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{v.staffUser || '—'}</td>
                      <td
                        style={{
                          fontSize: '0.85rem',
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={v.notes || ''}
                      >
                        {v.notes || '—'}
                      </td>
                      <td>
                        <button
                          onClick={() => openEditVisit(v)}
                          title="Edit staff & notes"
                          style={{
                            background: 'none',
                            border: '1px solid #ddd',
                            borderRadius: 5,
                            cursor: 'pointer',
                            padding: '0.2rem 0.4rem',
                            fontSize: '0.8rem',
                            color: '#666',
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit visit modal */}
      {editingVisit && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setEditingVisit(null)}
        >
          <div
            className="card"
            style={{ width: 420, maxWidth: '90vw', margin: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem', color: '#5B2C86', fontSize: '1rem' }}>
              Edit Visit #{editingVisit.visitSequence}
            </h3>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
                marginBottom: '0.75rem',
              }}
            >
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#555' }}>Staff</span>
              <input
                type="text"
                value={editStaff}
                onChange={(e) => setEditStaff(e.target.value)}
                placeholder="Who assisted?"
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  fontFamily: 'Montserrat, sans-serif',
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
                marginBottom: '1rem',
              }}
            >
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#555' }}>Notes</span>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
                placeholder="Add notes..."
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  fontFamily: 'Montserrat, sans-serif',
                  resize: 'vertical',
                }}
              />
            </label>

            {/* Service needs display */}
            {Array.isArray(editingVisit.serviceNeeds) && editingVisit.serviceNeeds.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <span
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#555',
                    display: 'block',
                    marginBottom: '0.4rem',
                  }}
                >
                  Service Needs
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {editingVisit.serviceNeeds.map((need) => (
                    <span
                      key={need}
                      style={{
                        background: '#e8f5e9',
                        color: '#2e7d32',
                        padding: '0.2rem 0.6rem',
                        borderRadius: 12,
                        fontSize: '0.8rem',
                        fontWeight: 500,
                      }}
                    >
                      {need}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="button-secondary"
                onClick={() => setEditingVisit(null)}
                style={{ padding: '0.4rem 1rem' }}
              >
                Cancel
              </button>
              <button
                className="button-primary"
                onClick={handleSaveVisit}
                disabled={savingVisit}
                style={{ padding: '0.4rem 1rem' }}
              >
                {savingVisit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit referral modal */}
      {editingReferral && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setEditingReferral(null)}
        >
          <div
            className="card"
            style={{ width: 420, maxWidth: '90vw', margin: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.25rem', color: '#5B2C86', fontSize: '1rem' }}>
              Edit Referral
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8125rem', color: '#666' }}>
              {editingReferral.referralType}
            </p>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
                marginBottom: '0.75rem',
              }}
            >
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#555' }}>Status</span>
              <select
                value={editReferralStatus}
                onChange={(e) => setEditReferralStatus(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  fontFamily: 'Montserrat, sans-serif',
                  background: 'white',
                }}
              >
                <option>Referred</option>
                <option>Engaged</option>
                <option>Completed</option>
                <option>Declined</option>
              </select>
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
                marginBottom: '1rem',
              }}
            >
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#555' }}>
                Staff Notes
              </span>
              <textarea
                value={editReferralNotes}
                onChange={(e) => setEditReferralNotes(e.target.value)}
                rows={3}
                placeholder="Add notes about this referral..."
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  fontFamily: 'Montserrat, sans-serif',
                  resize: 'vertical',
                }}
              />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="button-secondary"
                onClick={() => setEditingReferral(null)}
                style={{ padding: '0.4rem 1rem' }}
              >
                Cancel
              </button>
              <button
                className="button-primary"
                onClick={handleSaveReferral}
                disabled={savingReferral}
                style={{ padding: '0.4rem 1rem' }}
              >
                {savingReferral ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referrals */}
      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>Referrals ({referrals.length})</h3>

        {referralsLoading && <p style={{ color: '#888' }}>Loading referrals...</p>}

        {!referralsLoading && referrals.length === 0 && (
          <div className="fd-empty">
            <div className="fd-empty-icon">🤝</div>
            <p>No referrals for this client.</p>
          </div>
        )}

        {!referralsLoading && referrals.length > 0 && (
          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Referred</th>
                  <th>Status</th>
                  <th>Engaged</th>
                  <th>Completed</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.fdReferralId}>
                    <td>{r.referralType}</td>
                    <td>{new Date(r.referredDate).toLocaleDateString()}</td>
                    <td>
                      <span className={`fd-badge fd-badge-${r.engagementStatus.toLowerCase()}`}>
                        {r.engagementStatus}
                      </span>
                    </td>
                    <td>
                      {r.engagementDate ? new Date(r.engagementDate).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      {r.completionDate ? new Date(r.completionDate).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ maxWidth: 220 }} title={r.staffNotes || ''}>
                      <div
                        style={{
                          fontSize: '0.825rem',
                          color: '#555',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.staffNotes || '—'}
                      </div>
                      {r.notesUpdatedBy && (
                        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 2 }}>
                          {r.notesUpdatedBy}
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => openEditReferral(r)}
                        title="Edit referral"
                        style={{
                          background: 'none',
                          border: '1px solid #ddd',
                          borderRadius: 5,
                          cursor: 'pointer',
                          padding: '0.2rem 0.4rem',
                          fontSize: '0.8rem',
                          color: '#666',
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
